import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * "Task" from a call-log row: a new task with the caller's number (and saved
 * name) already filled in. Employees see the number masked and can't edit it;
 * the server takes the real number from the call (from_call_id).
 */
export default function TaskFromCallDialog({ call, owner = false, selfId, onClose, onCreated }) {
  const masked = (call?.customer_phone || "").includes("•");
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!call) return;
    setForm({
      title: "",
      description: "",
      customer_name: call.customer_name || "",
      customer_phone: call.customer_phone || "",
      assigned_to: owner ? "" : selfId || "",
      priority: "medium",
    });
    api
      .get("/employees")
      .then(({ data }) => {
        setEmployees(data || []);
        if (owner && data?.[0]) setForm((f) => (f.assigned_to ? f : { ...f, assigned_to: data[0].id }));
      })
      .catch(() => {});
  }, [call, owner, selfId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.title.trim() || !form.customer_name.trim() || !form.assigned_to) {
      toast.error("Fill in title, customer name and who it's for");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/tasks", { ...form, from_call_id: call.id });
      toast.success(`Task #${data.task_no} created`);
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create the task");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!call} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="task-from-call-dialog">
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input
              value={form.title || ""}
              onChange={set("title")}
              placeholder="e.g. CCTV not recording"
              autoFocus
              data-testid="task-from-call-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Customer name</Label>
              <Input
                value={form.customer_name || ""}
                onChange={set("customer_name")}
                data-testid="task-from-call-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input
                value={form.customer_phone || ""}
                onChange={set("customer_phone")}
                readOnly={masked}
                className={masked ? "bg-slate-50 text-slate-500" : ""}
                data-testid="task-from-call-phone"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Assign to</Label>
              <Select
                value={form.assigned_to || ""}
                onValueChange={(v) => setForm((f) => ({ ...f, assigned_to: v }))}
              >
                <SelectTrigger data-testid="task-from-call-assignee">
                  <SelectValue placeholder={employees.length ? "Choose" : "Loading…"} />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                      {e.id === selfId ? " (me)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Priority</Label>
              <Select
                value={form.priority || "medium"}
                onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}
              >
                <SelectTrigger data-testid="task-from-call-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Customer problem / notes</Label>
            <Textarea
              value={form.description || ""}
              onChange={set("description")}
              rows={3}
              data-testid="task-from-call-notes"
            />
          </div>
          <Button
            className="w-full bg-[#0055FF] hover:bg-[#0040CC]"
            onClick={submit}
            disabled={saving}
            data-testid="task-from-call-submit"
          >
            {saving ? "Assigning…" : "Assign task"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
